import { useState, useEffect } from 'react';
import { Calculator, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { getLanguageFromPath } from '../lib/i18n';

export default function Beregner({ lang }) {
  const location = useLocation();
  const currentLang = lang || getLanguageFromPath(location.pathname);

  const TEXTS = {
    da: {
      title: 'Beregn din payback',
      subtitle: 'Indtast dine egne tal herunder — så regner vi resten med det samme.',
      boxesLabel: 'Antal bokse (stk)',
      beddingUsageLabel: 'Forbrug strøelse (kg/md/boks)',
      beddingPriceLabel: 'Pris strøelse (kr/kg)',
      muckEmptiesLabel: 'Tømninger af mødding pr. år',
      muckPriceLabel: 'Pris pr. tømning (kr)',
      timeSpentLabel: 'Tidsforbrug (min/dag/boks)',
      hourlyRateLabel: 'Timepris (kr)',
      calculationTitle: 'Din beregning',
      annualSavings: 'Din besparelse pr. år',
      paybackTime: 'Tilbagebetalingstid',
      monthSingular: 'måned',
      monthPlural: 'måneder',
      currentCosts: 'Dine omkostninger i dag',
      perYear: '/ år',
      note: 'Beregningen er vejledende og baseret på erfaringstal fra Danshaker.',
      cta: 'Book gratis demo nu',
      ctaHref: '/gratis-demo',
      disclaimerTitle: 'Ansvarsfraskrivelse (Disclaimer):',
      disclaimerText: 'Beregningen er vejledende og baseret på gennemsnitlige erfaringstal fra vores kunder. De faktiske besparelser kan variere afhængigt af staldforhold, strøelsestype, individuelle arbejdsvaner samt priser på strøelse og arbejdskraft. Alle beregninger tager udgangspunkt i en maskinpris på 31.495,- kr. ekskl. moms.'
    },
    en: {
      title: 'Calculate your payback',
      subtitle: 'Enter your own stable numbers below — we will calculate your savings instantly.',
      boxesLabel: 'Number of horse stalls / boxes',
      beddingUsageLabel: 'Bedding usage (kg/month/stall)',
      beddingPriceLabel: 'Bedding price (€/kg)',
      muckEmptiesLabel: 'Manure container empties per year',
      muckPriceLabel: 'Cost per emptying (€)',
      timeSpentLabel: 'Sorting time (min/day/stall)',
      hourlyRateLabel: 'Hourly labor cost (€/hr)',
      calculationTitle: 'Your calculation',
      annualSavings: 'Your annual savings',
      paybackTime: 'Payback period',
      monthSingular: 'month',
      monthPlural: 'months',
      currentCosts: 'Your costs today',
      perYear: '/ year',
      note: 'The calculation is indicative and based on average customer figures from Danshaker.',
      cta: 'Book free demo now',
      ctaHref: '/en/free-demo',
      disclaimerTitle: 'Disclaimer:',
      disclaimerText: 'The calculation is indicative and based on empirical data from Danshaker customers. Actual savings may vary depending on stable conditions, bedding type, daily routines, and local costs. Based on a standard machine price of €4,225 excl. VAT.'
    },
    de: {
      title: 'Berechnen Sie Ihre Amortisation',
      subtitle: 'Geben Sie unten Ihre eigenen Stallzahlen ein – wir berechnen Ihre Ersparnis sofort.',
      boxesLabel: 'Anzahl Pferdeboxen (Stk.)',
      beddingUsageLabel: 'Einstreuverbrauch (kg/Monat/Box)',
      beddingPriceLabel: 'Einstreupreis (€/kg)',
      muckEmptiesLabel: 'Mistplatz-Entleerungen pro Jahr',
      muckPriceLabel: 'Kosten pro Entleerung (€)',
      timeSpentLabel: 'Zeitaufwand (Min./Tag/Box)',
      hourlyRateLabel: 'Stundensatz (€/Std.)',
      calculationTitle: 'Ihre Berechnung',
      annualSavings: 'Ihre jährliche Ersparnis',
      paybackTime: 'Amortisationszeit',
      monthSingular: 'Monat',
      monthPlural: 'Monate',
      currentCosts: 'Ihre aktuellen Kosten heute',
      perYear: '/ Jahr',
      note: 'Die Berechnung ist ein Richtwert und basiert auf Durchschnittswerten von Danshaker-Kunden.',
      cta: 'Kostenlose Demo buchen',
      ctaHref: '/de/kostenlose-demo',
      disclaimerTitle: 'Haftungsausschluss:',
      disclaimerText: 'Die Berechnung ist unverbindlich und basiert auf Durchschnittswerten unserer Kunden. Tatsächliche Einsparungen können je nach Stallbedingungen, Einstreuart, Arbeitsgewohnheiten und örtlichen Preisen variieren. Basis ist der Maschinenpreis von 4.225 € zzgl. MwSt.'
    }
  };

  const t = TEXTS[currentLang] || TEXTS.da;
  const isEuro = currentLang === 'en' || currentLang === 'de';

  // Input states with default values
  const [antalBokse, setAntalBokse] = useState(5);
  const [forbrugStroelse, setForbrugStroelse] = useState(50); // kg/md/boks
  const [prisStroelse, setPrisStroelse] = useState(isEuro ? 0.52 : 3.87); // €/kg or kr/kg
  const [toemningerMoedding, setToemningerMoedding] = useState(4); // pr. år
  const [prisToemning, setPrisToemning] = useState(isEuro ? 335 : 2500); // € or kr. pr tømning
  const [tidsforbrug, setTidsforbrug] = useState(15); // min/dag/boks
  const [timepris, setTimepris] = useState(isEuro ? 20 : 150); // €/time or kr/time

  // Result states
  const [omkostningerIDag, setOmkostningerIDag] = useState(0);
  const [besparelseAar, setBesparelseAar] = useState(0);
  const [tilbagebetalingstid, setTilbagebetalingstid] = useState(0);

  useEffect(() => {
    // Calculate costs based on formulas
    const stroelseOmkostningerAar = antalBokse * forbrugStroelse * prisStroelse * 12;
    const moeddingOmkostningerAar = toemningerMoedding * prisToemning;
    const tidsOmkostningerAar = antalBokse * (tidsforbrug / 60) * 365 * timepris;

    const samletOmkostningAar = Math.round(stroelseOmkostningerAar + moeddingOmkostningerAar + tidsOmkostningerAar);
    
    // Danshaker saves 50% on average
    const samletBesparelseAar = Math.round(samletOmkostningAar * 0.5);
    
    // Payback time based on a machine price of 31.495 kr. ex moms or ~4.225 €
    const maskinePris = isEuro ? 4225 : 31495;
    const paybackMdr = samletBesparelseAar > 0 
      ? Math.max(1, Math.ceil((maskinePris / samletBesparelseAar) * 12)) 
      : 0;

    setOmkostningerIDag(samletOmkostningAar);
    setBesparelseAar(samletBesparelseAar);
    setTilbagebetalingstid(paybackMdr);
  }, [antalBokse, forbrugStroelse, prisStroelse, toemningerMoedding, prisToemning, tidsforbrug, timepris, isEuro]);

  const formatKroner = (val) => {
    if (isEuro) {
      const locale = currentLang === 'de' ? 'de-DE' : 'en-IE';
      return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val);
    }
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(val);
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12">
      {/* Input panel */}
      <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-xl lg:col-span-7 md:p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-xl bg-[#0E67A2]/10 p-2.5 text-[#0E67A2]">
            <Calculator className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900">{t.title}</h3>
            <p className="text-sm text-slate-700 font-medium">{t.subtitle}</p>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          {/* Antal bokse */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-1.5">{t.boxesLabel}</label>
            <input
              type="number"
              min="1"
              value={antalBokse || ''}
              onChange={(e) => setAntalBokse(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-base font-bold text-slate-900 focus:border-[#0E67A2] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0E67A2] transition"
            />
          </div>

          {/* Forbrug strøelse */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-1.5">{t.beddingUsageLabel}</label>
            <input
              type="number"
              min="0"
              value={forbrugStroelse || ''}
              onChange={(e) => setForbrugStroelse(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-base font-bold text-slate-900 focus:border-[#0E67A2] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0E67A2] transition"
            />
          </div>

          {/* Pris strøelse */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-1.5">{t.beddingPriceLabel}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={prisStroelse || ''}
              onChange={(e) => setPrisStroelse(Math.max(0, parseFloat(e.target.value) || 0))}
              className="w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-base font-bold text-slate-900 focus:border-[#0E67A2] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0E67A2] transition"
            />
          </div>

          {/* Tømninger af mødding pr. år */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-1.5">{t.muckEmptiesLabel}</label>
            <input
              type="number"
              min="0"
              value={toemningerMoedding || ''}
              onChange={(e) => setToemningerMoedding(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-base font-bold text-slate-900 focus:border-[#0E67A2] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0E67A2] transition"
            />
          </div>

          {/* Pris pr. tømning */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-1.5">{t.muckPriceLabel}</label>
            <input
              type="number"
              min="0"
              value={prisToemning || ''}
              onChange={(e) => setPrisToemning(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-base font-bold text-slate-900 focus:border-[#0E67A2] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0E67A2] transition"
            />
          </div>

          {/* Tidsforbrug */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-1.5">{t.timeSpentLabel}</label>
            <input
              type="number"
              min="0"
              value={tidsforbrug || ''}
              onChange={(e) => setTidsforbrug(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-base font-bold text-slate-900 focus:border-[#0E67A2] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0E67A2] transition"
            />
          </div>

          {/* Timepris */}
          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-slate-900 mb-1.5">{t.hourlyRateLabel}</label>
            <input
              type="number"
              min="0"
              value={timepris || ''}
              onChange={(e) => setTimepris(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-base font-bold text-slate-900 focus:border-[#0E67A2] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0E67A2] transition"
            />
          </div>
        </div>
      </div>

      {/* Result panel */}
      <div className="flex flex-col justify-between rounded-2xl border border-[#0E67A2]/30 bg-gradient-to-b from-white to-slate-100 p-6 shadow-xl lg:col-span-5 md:p-8">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-widest text-slate-700">{t.calculationTitle}</h4>
          
          <div className="mt-6 space-y-6">
            <div>
              <p className="text-sm text-slate-700 font-semibold">{t.annualSavings}</p>
              <p className="text-4xl font-extrabold text-[#0E67A2] md:text-5xl">{formatKroner(besparelseAar)}</p>
            </div>

            <div className="border-t border-slate-300 pt-6">
              <p className="text-sm text-slate-700 font-semibold">{t.paybackTime}</p>
              <p className="text-3xl font-bold text-slate-900">
                {tilbagebetalingstid} {tilbagebetalingstid === 1 ? t.monthSingular : t.monthPlural}
              </p>
            </div>

            <div className="border-t border-slate-300 pt-6">
              <p className="text-sm text-slate-700 font-semibold">{t.currentCosts}</p>
              <p className="text-xl font-bold text-slate-800">{formatKroner(omkostningerIDag)} {t.perYear}</p>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-4">
          <div className="flex items-start gap-2.5 text-xs text-slate-700 font-medium">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-[#0E67A2]" />
            <p>{t.note}</p>
          </div>

          <Link
            to={t.ctaHref}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0E67A2] py-4 text-center font-bold text-white transition hover:bg-[#0E67A2]/90 shadow-md"
          >
            {t.cta}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="lg:col-span-12 mt-4 text-xs text-slate-700 leading-relaxed border-t border-slate-300 pt-4 font-medium">
        <p className="font-bold text-slate-900 mb-1">{t.disclaimerTitle}</p>
        <p>
          {t.disclaimerText}
        </p>
      </div>
    </div>
  );
}
